/**
 * NotificationDetailModal — iter186
 *
 * Shows the full notification body and, if the notification carries
 * an `action_url`, a contextual CTA button (e.g. "ابدأ التوثيق").
 *
 * When the CTA is clicked we do a lightweight best-effort probe on
 * common resource routes (vehicles, disputes, campaigns, businesses,
 * private-offers, verifications, messages) to detect deleted or
 * already-processed targets so the user sees a proper Arabic message
 * instead of landing on a 404 page.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowUpRight, Loader2, AlertCircle } from "lucide-react";
import { apiClient } from "../lib/api";

// iter223 — same helper used by NotificationsPage. Prefers the
// backend-stored i18n bundle for the active UI language and falls
// back to the legacy plain field on old rows so mixed-language
// output never happens.
function pickI18n(bundle, plain, lang) {
  if (bundle && typeof bundle === "object") {
    return bundle[lang] || bundle.ar || bundle.en || plain || "";
  }
  return plain || "";
}

const CATEGORY_LABEL = {
  account:  { ar: "الحساب",        en: "Account" },
  kyc:      { ar: "التوثيق",       en: "Verification" },
  listings: { ar: "الإعلانات",     en: "Listings" },
  messages: { ar: "الرسائل",       en: "Messages" },
  disputes: { ar: "النزاعات",      en: "Disputes" },
  payments: { ar: "المدفوعات",     en: "Payments" },
  admin:    { ar: "الإدارة",        en: "Admin" },
};

// Frontend route → optional API probe. When we know how to look the
// resource up, we do a HEAD-equivalent GET to detect deleted/handled
// data. Keys are matched as `pathname.startsWith(prefix)`.
const PROBE_MAP = [
  { prefix: "/vehicles/",       toApi: (p) => `/vehicles/${p.split("/")[2] || ""}` },
  { prefix: "/disputes/",       toApi: (p) => `/disputes/${p.split("/")[2] || ""}` },
  { prefix: "/business/",       toApi: (p) => `/platform/dashboard/business/${p.split("/")[2] || ""}/home` },
  { prefix: "/campaigns/",      toApi: (p) => `/admin/campaigns/${p.split("/")[2] || ""}` },
  { prefix: "/private-offers/", toApi: (p) => `/me/private-offers` },
  { prefix: "/kyc",             toApi: () => `/kyc/me` },
  { prefix: "/verifications/",  toApi: (p) => `/verifications/${p.split("/")[2] || ""}` },
];

function ctaLabel(n, lang) {
  const kind = (n.kind || "").toLowerCase();
  const cat = (n.category || "").toLowerCase();
  const url = String(n.action_url || "");
  if (kind === "listing.boost_expired" || url.endsWith("/boost")) {
    return lang === "ar" ? "اختر باقة التعزيز" : "Choose a boost package";
  }
  // KYC / verification
  if (cat === "kyc" || kind.includes("kyc") || url.includes("/kyc") || url.includes("/verifications")) {
    return lang === "ar" ? "ابدأ التوثيق" : "Start verification";
  }
  if (cat === "listings" || url.includes("/vehicles")) {
    return lang === "ar" ? "عرض الإعلان" : "View listing";
  }
  if (cat === "disputes" || url.includes("/disputes")) {
    return lang === "ar" ? "فتح النزاع" : "Open dispute";
  }
  if (cat === "messages" || url.includes("/messages")) {
    return lang === "ar" ? "عرض الرسالة" : "Open message";
  }
  if (cat === "payments" || url.includes("/payments") || url.includes("/wallet")) {
    return lang === "ar" ? "عرض التفاصيل المالية" : "View payment";
  }
  if (kind.includes("private_offer") || url.includes("/private-offers")) {
    return lang === "ar" ? "الانتقال إلى العرض" : "View offer";
  }
  if (kind.includes("campaign") || url.includes("/campaigns")) {
    return lang === "ar" ? "عرض الحملة" : "View campaign";
  }
  return lang === "ar" ? "الانتقال" : "Open";
}

export default function NotificationDetailModal({ notification, onClose, lang }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(null);

  if (!notification) return null;

  const rawUrl = String(notification.action_url || "");
  const pathname = rawUrl.replace(/^https?:\/\/[^/]+/, "") || null;
  const catMeta = CATEGORY_LABEL[notification.category] || { ar: notification.category, en: notification.category };

  const goToAction = async () => {
    if (!pathname || busy) return;
    setBusy(true);
    setStale(null);
    // Best-effort probe: only probe when we know how to. Any error
    // other than 404 just falls through to normal navigation.
    const probe = PROBE_MAP.find((p) => pathname.startsWith(p.prefix));
    let deleted = false;
    if (probe) {
      try {
        await apiClient.get(probe.toApi(pathname));
      } catch (e) {
        if (e?.response?.status === 404) deleted = true;
      }
    }
    setBusy(false);
    if (deleted) {
      setStale(
        lang === "ar"
          ? "تم حذف هذا العنصر أو معالجته مسبقاً ولم يعد متاحاً."
          : "This item has been deleted or already processed and is no longer available."
      );
      return;
    }
    navigate(pathname || "/notifications");
    onClose?.();
  };

  const label = ctaLabel(notification, lang);

  return (
    <div className="fixed inset-0 z-[100]" data-testid="notif-detail-modal">
      <button
        type="button"
        aria-label="close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <div className="absolute inset-x-4 top-16 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-24 sm:w-[460px] rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="p-5 flex items-start justify-between border-b border-[#0a0a0a]/[0.06]">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-bold">
              {lang === "ar" ? catMeta.ar : catMeta.en}
            </div>
            <h3 className="text-lg font-display font-bold text-[#0a0a0a] mt-1 leading-snug">
              {pickI18n(notification.title_i18n, notification.title, lang)}
            </h3>
            <div className="text-[11px] text-[#a8a8a8] mt-1">
              {new Date(notification.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#8a8a8a] hover:text-[#0a0a0a] shrink-0"
            aria-label="close"
            data-testid="notif-detail-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          <p
            className="text-sm text-[#3a3a3a] whitespace-pre-wrap leading-relaxed"
            data-testid="notif-detail-body"
          >
            {pickI18n(notification.body_i18n, notification.body, lang)}
          </p>

          {stale && (
            <div
              className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-[13px] text-red-800 flex items-start gap-2"
              data-testid="notif-detail-stale"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{stale}</span>
            </div>
          )}
        </div>

        {pathname && !stale && (
          <div className="flex justify-end gap-2 px-5 pb-5">
            <button
              type="button"
              onClick={goToAction}
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold shadow-sm disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
              data-testid="notif-detail-cta"
            >
              {busy
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ArrowUpRight className="w-4 h-4" />}
              {label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
